import { LitElement, html, css } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import {
  CONTACT_REP_HASH,
  openContactRepModal,
} from '../../../../helpers/contact-rep-modal';

class CtaTextWithIcon extends LitElement {
  static get properties() {
    return {
      hidden: { type: Boolean },
      disabled: { type: Boolean },
      ctaData: { type: Object },
      hovering: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.hovering = false;
    this.disabled = false;
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      css`
        a {
          display: flex;
          flex-direction: column;
          justify-content: center;
          cursor: pointer;
        }

        a > .cta-button {
          width: 100%;
          display: flex;
          white-space: nowrap;
        }

        a > .cta-button > p {
          /* 1em (16px) is intentionally larger than the 14px CTA token, so
             only tracking/weight are tokenized here. */
          font-size: 1em;
          letter-spacing: var(--ls-cta);
          line-height: 1.5em;
          font-weight: var(--fw-medium);
        }

        a:hover:not(.disabled) > .cta-button > p {
          /* Was 600: no 600 face is loaded so the browser synthesized it;
             keeping the real medium weight is an intended fix. */
          font-weight: var(--fw-medium);
          text-decoration: underline;
        }

        img {
          width: calc(24rem / var(--default-rem-px-count));
          aspect-ratio: 1/1;
          padding-right: calc(8rem / var(--default-rem-px-count));
        }

        a.disabled {
          cursor: default;
        }
      `,
    ];
  }

  render() {
    return !!this.parsedCtaData
      ? html`
          <a
            href=${ifDefined(this.urlString)}
            target=${ifDefined(this.targetValue)}
            class="${classMap(this.linkClasses)}"
            style="${styleMap(this.buttonWidth)}"
            @click="${this._handleClick}"
            @mouseover="${this._handleHover}"
            @mouseleave="${this._handleLeave}"
            .disabled="${this.disabled}"
          >
            <span class="cta-button">
              ${this.ctaIconUrl
                ? html`
                    <img
                      src=${!this.hovering
                        ? this.ctaIconUrl
                        : this.ctaIconHoverUrl}
                    />
                  `
                : html``}
              <p>
                ${this.textString}
              </p>
            </span>
          </a>
        `
      : html``;
  }

  _handleClick() {
    if (this.urlString === CONTACT_REP_HASH) {
      openContactRepModal();
    }
  }

  _handleHover() {
    this.hovering = true;
  }

  _handleLeave() {
    this.hovering = false;
  }

  get isPrimary() {
    return (
      this.getAttribute('cta-type') === 'primary' || this.ctaData.isPrimary
    );
  }

  get buttonWidth() {
    return this.ctaData.fullWidth
      ? { width: '100%' }
      : { width: 'max-content' };
  }

  get svgUrl() {
    return this.isPrimary
      ? '/static/icons/SVG/arrow-white-5x10.svg'
      : '/static/icons/SVG/arrow-black-5x10.svg';
  }

  get ctaIconUrl() {
    const urlsIdentifiersArray = [
      ['Sample', 'order_sample'],
      ['Panel', 'order_panel'],
      ['Contact', 'contact_rep'],
      ['Quote', 'request_quote'],
      ['Cart', 'add_to_cart'],
      ['Unavailable', 'out_of_stock'],
      ['Visualize', 'visualize'],
      ['Design Starters', 'design_starters'],
    ];

    const iconIdentifier = urlsIdentifiersArray.find(([name]) =>
      this.textString.includes(name)
    );
    return iconIdentifier ? `/static/icons/SVG/${iconIdentifier[1]}.svg` : null;
  }

  get ctaIconHoverUrl() {
    return this.ctaIconUrl.replace('.svg', '_hover.svg');
  }

  get textString() {
    return this.ctaData.text || '';
  }

  get urlString() {
    return this.ctaData.url || undefined;
  }

  get targetValue() {
    return this.ctaData.target || '_self';
  }

  get linkClasses() {
    return {
      'primary-colors': this.isPrimary,
      'secondary-colors': !this.isPrimary,
      hidden: this.hidden,
      disabled: this.disabled,
      'print-none': !this.reserveSpaceWhenHidden,
      'print-hidden': this.reserveSpaceWhenHidden,
    };
  }

  get parsedCtaData() {
    if (this.ctaData) return this.ctaData;

    this.ctaData = this.getAttribute('cta-data')
      ? JSON.parse(this.getAttribute('cta-data'))
      : null;

    return this.ctaData;
  }

  get reserveSpaceWhenHidden() {
    return this.hasAttribute('reserve-space-when-hidden');
  }
}
customElements.define('cta-text-with-icon', CtaTextWithIcon);
